import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import {
  calculateRemainingResource,
  getMergePackageName,
  getPackageName,
} from '@src/services/package';
import {
  BUSINESS_PACKAGE_CODES,
  FREE_PACKAGE_CODES,
  PACKAGE_DURATION,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { SUBSCRIPTION_PURCHASE_STATUS } from '@src/constants/subscription';
import {
  checkAutoRenewOrder,
  checkInAppPurchase,
} from '@src/services/subscription';
import { delimitNumber } from '@src/utils/number';
import { CONTACT_US_PAGE } from '@src/configs';
import useUserInfo from '@src/hooks/useTtsUser';
import route from '@src/constants/route';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import PackageV3 from './PackageV3';
import PackageContainer from './components/PackageContainer';
import PackageV2 from './PackageV2';
import AutoRenewPackageInfo from './AutoRenewPackageInfo';

const SubscriptionItemV2V3 = ({
  packageInfo = {},
  usingOrder = {},
  hasV2Package,
  totalRemainingCredits,
  nextResetAt,
  purchasedTopUpCredits = 0,
  isMobile,
  subscriptionPurchase,
  totalCapturedRolloverCredits = 0,
  capturedReferralCreditsCycle = 0,
}) => {
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { lockCharacters = 0 } = user;
  const { packageExpiryDate, paymentMethod } = usingOrder;
  const { type, code, level } = packageInfo;
  const { type: paymentMethodType } = paymentMethod || {};
  const { handleGetTopUpPackageCode } = useMappingPackage();

  const { ttsUser } = useUserInfo();

  const { studio } = ttsUser || {};
  const { cycle } = studio || {};
  const { creditsPerCycle } = cycle || {};
  const { oneTime } = studio || {};

  const isInAppPurchase = checkInAppPurchase(
    packageInfo?.type,
    paymentMethodType,
  );

  const oneTimeExpiryDate = oneTime?.packageExpiryDate || packageExpiryDate;

  const hasOneTimeExpired =
    oneTimeExpiryDate && moment(oneTimeExpiryDate).isBefore(moment());
  const isFreePackage = FREE_PACKAGE_CODES.includes(code);
  const isLockedOneTimeCredit = hasOneTimeExpired && !!lockCharacters;
  const isBusinessPackage = BUSINESS_PACKAGE_CODES.includes(code);

  const isCancelAutoRenew =
    subscriptionPurchase?.status === SUBSCRIPTION_PURCHASE_STATUS.CANCELED;

  const isMonthlyPackage = code?.includes(PACKAGE_DURATION.MONTH);

  const handleRedirectToBuyPackage = () => {
    const redirectUrl = `${route.PAYMENT}`;
    if (type === PACKAGE_TYPE.API) {
      window.open(redirectUrl);
    } else {
      history.push(redirectUrl);
    }
  };

  const handleClickBuyMoreChar = () => {
    const topUpPackageCode = handleGetTopUpPackageCode({
      type,
      level,
    });
    const redirectUrl = `${route.PAYMENT}?package=${topUpPackageCode}`;
    history.push(redirectUrl);
  };

  const handleClickContactSupport = () =>
    window.open(CONTACT_US_PAGE, '_blank', 'noopener,noreferrer');

  const remainingResource = calculateRemainingResource(
    type,
    user,
    hasOneTimeExpired,
  );

  const totalCredits =
    creditsPerCycle +
    purchasedTopUpCredits +
    totalCapturedRolloverCredits +
    capturedReferralCreditsCycle;

  const usedCredits =
    totalCredits - totalRemainingCredits > 0
      ? totalCredits - totalRemainingCredits
      : 0;
  const usedCreditsPercent = ((usedCredits / totalCredits) * 100).toFixed(2);
  const hasNextResetCycle = moment(nextResetAt).isBefore(
    packageExpiryDate,
    'day',
  );

  const renderPackageButton = () => {
    if (isBusinessPackage) {
      return (
        <Button
          fullWidth
          className="buy-more-btn"
          onClick={handleClickContactSupport}
          data-id="button-buy"
          data-current-package-code={code}
        >
          {t('contact')}
        </Button>
      );
    }

    if (isFreePackage || hasOneTimeExpired) {
      return (
        <Button
          fullWidth
          className="buy-more-btn"
          onClick={handleRedirectToBuyPackage}
          data-id="button-buy"
          data-current-package-code={code}
        >
          {t('buyNow')}
        </Button>
      );
    }

    return (
      <Button
        fullWidth
        className="buy-more-btn"
        onClick={handleClickBuyMoreChar}
        data-id="button-buy"
        data-current-package-code={code}
      >
        {t('buyMoreCharacters')}
      </Button>
    );
  };

  const isAutoRenewalPackage =
    usingOrder && checkAutoRenewOrder(usingOrder?.renewalType);

  const renderDescription = () => (
    <Typography className="credit-per-cycle">
      {isMonthlyPackage && !isAutoRenewalPackage ? (
        <span>
          {t('creditsPerMonth', {
            creditsPerMonth: delimitNumber(creditsPerCycle, language),
          })}
        </span>
      ) : (
        <span>
          {t('creditsPerCycle', {
            creditsPerCycle: delimitNumber(creditsPerCycle, language),
          })}
        </span>
      )}
    </Typography>
  );

  return (
    <>
      <PackageContainer
        packageName={getPackageName(
          packageInfo?.name,
          packageInfo?.code,
          language,
          t,
          true,
          true,
        )}
        productType={t(`${getMergePackageName(type)}`)}
        renderDescription={renderDescription}
        renderButton={renderPackageButton}
        packageDetail={
          <>
            <Box className="package-wrapper">
              <Typography className="title">{t('currentPlan')}</Typography>
              <PackageV3
                wallet={{
                  usedCredits,
                  totalCredits,
                  creditsPerCycle,
                  nextResetAt,
                  usedCreditsPercent,
                  hasNextResetCycle,
                  packageExpiryDate,
                  purchasedTopUpCredits,
                  capturedReferralCreditsCycle,
                }}
                hasV2Package={hasV2Package}
                isMonthlyPackage={isMonthlyPackage}
                isMobile={isMobile}
                isAutoRenewalPackage={isAutoRenewalPackage}
              />
              {(!hasOneTimeExpired ||
                (!isInAppPurchase && !isCancelAutoRenew)) && (
                <AutoRenewPackageInfo
                  packageExpiryDate={packageExpiryDate}
                  subscriptionPurchase={subscriptionPurchase}
                  packageInfo={packageInfo}
                  paymentMethodType={paymentMethodType}
                  orderInfo={usingOrder}
                  isInAppPurchase={isInAppPurchase}
                />
              )}
            </Box>
            <Box className="package-wrapper">
              <Typography className="title">
                {`${t('oldPlan')} `}
                {oneTime?.packageCode && (
                  <span className="old-plan-package-name">
                    {`(${getPackageName(
                      null,
                      oneTime?.packageCode,
                      language,
                      t,
                      true,
                      true,
                    )})`}
                  </span>
                )}
              </Typography>
              <PackageV2
                isLocked={isLockedOneTimeCredit}
                credits={remainingResource}
                packageExpiryDate={oneTimeExpiryDate}
                isMobile={isMobile}
              />
            </Box>
          </>
        }
        isMobile={isMobile}
      />
    </>
  );
};

export default SubscriptionItemV2V3;
